import { useState } from "react"

import { cn, scale, starter } from "@productlane/lib/src/browser"
import { Input, Switch } from "@productlane/ui"

import { isAllowedDomainsList } from "@/components/shared/allowed-domains"
import { UpgradeButton } from "@/components/upgrade-button"
import { useRequiredPlans } from "@/lib/plans"
import { useAutosaveText } from "@/lib/use-autosave-text"
import { SettingItem } from "@/screens/settings/settings-switches/setting-item"
import { SettingsContainer } from "@/screens/settings/settings-switches/settings-container"
import { SettingsSection } from "@/screens/settings/settings-switches/settings-section"
import { isValidLinkUrl, normalizeLinkUrl } from "./portal-link-url"

export interface PortalAuthValues {
	isGoogleAuthRequired: boolean
	googleDomain: string | null
}

export interface PortalLinkValues {
	usePortalLogoUrl: boolean
	portalLogoUrl: string | null
	changelogLink1Description: string | null
	changelogLink1Url: string | null
	changelogLink2Description: string | null
	changelogLink2Url: string | null
}

/**
 * The inherit switch a named portal shows above its fields. The main portal is
 * the source everyone else inherits, so it passes none.
 */
export interface Gate {
	checked: boolean
	onChange: (checked: boolean) => void
}

export function PortalAuthSection({
	values,
	onSave,
	gate,
}: {
	values: PortalAuthValues
	onSave: (args: Partial<PortalAuthValues>) => void
	gate?: Gate
}) {
	// The main portal's sign-in rules are a Starter feature. Only the per-portal
	// override is Scale, because running several portals is itself Scale.
	const { passes: hasRequiredPlan } = useRequiredPlans(
		gate ? scale.andUp : starter.andUp,
	)
	const showFields = !gate || gate.checked

	return (
		<>
			<SettingsSection
				title="Authentication"
				description="Who can open this portal."
				className="mb-3"
			/>
			<SettingsContainer className="flex flex-col gap-4 divide-y-0">
				{gate && (
					<SettingItem
						title="Own authentication"
						description="Use the rules below instead of the main portal's."
						action={
							// Every portal-instance write asserts Scale, so below it the
							// switch would flip back the moment the server refused.
							hasRequiredPlan ? (
								<Switch
									name="overridesAuth"
									label={null}
									className="pr-1"
									checked={gate.checked}
									onCheckedChange={gate.onChange}
								/>
							) : (
								<UpgradeButton required={scale.andUp} />
							)
						}
					/>
				)}

				{showFields && (
					<SettingItem
						title="Require sign-in"
						description="Visitors sign in before they can open this portal."
						action={
							hasRequiredPlan ? (
								<Switch
									name="isGoogleAuthRequired"
									label={null}
									className="pr-1"
									checked={values.isGoogleAuthRequired}
									onCheckedChange={(isGoogleAuthRequired) =>
										onSave({ isGoogleAuthRequired })
									}
								/>
							) : (
								<UpgradeButton required={gate ? scale.andUp : starter.andUp} />
							)
						}
					/>
				)}

				{/* Preserve editing of an existing main-portal restriction after a downgrade. */}
				{showFields &&
					(!gate || hasRequiredPlan) &&
					values.isGoogleAuthRequired && (
						<AutosaveField
							label="Who can sign in"
							hint="Add a domain for a whole team or an address for one person."
							placeholder="acme.com, jane@gmail.com"
							value={values.googleDomain ?? ""}
							validate={isAllowedDomainsList}
							invalidHint="Separate entries with commas, like acme.com, jane@gmail.com"
							onSave={(googleDomain) =>
								onSave({ googleDomain: googleDomain.trim() || null })
							}
						/>
					)}
			</SettingsContainer>
		</>
	)
}

export function PortalLinksSection({
	values,
	onSave,
	gate,
}: {
	values: PortalLinkValues
	onSave: (args: Partial<PortalLinkValues>) => void
	gate?: Gate
}) {
	const { passes: hasRequiredPlan } = useRequiredPlans(scale.andUp)
	const showFields = !gate || gate.checked

	return (
		<>
			<SettingsSection
				title="Custom links"
				description="Where the logo and the footer links point."
				className="mb-3"
			/>
			<SettingsContainer className="flex flex-col gap-4 divide-y-0">
				{gate && (
					<SettingItem
						title="Own links"
						description="Use the links below instead of the main portal's."
						action={
							hasRequiredPlan ? (
								<Switch
									name="overridesCustomLinks"
									label={null}
									className="pr-1"
									checked={gate.checked}
									onCheckedChange={gate.onChange}
								/>
							) : (
								<UpgradeButton required={scale.andUp} />
							)
						}
					/>
				)}

				{showFields && (
					<>
						<SettingItem
							title="Custom home link"
							description="The logo points at your own page instead of this portal."
							action={
								<Switch
									name="usePortalLogoUrl"
									label={null}
									className="pr-1"
									checked={values.usePortalLogoUrl}
									onCheckedChange={(usePortalLogoUrl) =>
										onSave({ usePortalLogoUrl })
									}
								/>
							}
						/>

						{values.usePortalLogoUrl && (
							<AutosaveField
								label="Home link URL"
								placeholder="https://acme.com"
								value={values.portalLogoUrl ?? ""}
								validate={isValidLinkUrl}
								invalidHint="Enter a full address, like acme.com/login"
								onSave={(raw) =>
									onSave({ portalLogoUrl: normalizeLinkUrl(raw) })
								}
							/>
						)}

						<LinkPair
							label="Footer link 1"
							title={values.changelogLink1Description ?? ""}
							url={values.changelogLink1Url ?? ""}
							placeholder="https://acme.com/contact"
							onSaveTitle={(changelogLink1Description) =>
								onSave({
									changelogLink1Description:
										changelogLink1Description.trim() || null,
								})
							}
							onSaveUrl={(raw) =>
								onSave({ changelogLink1Url: normalizeLinkUrl(raw) })
							}
						/>

						<LinkPair
							label="Footer link 2"
							title={values.changelogLink2Description ?? ""}
							url={values.changelogLink2Url ?? ""}
							placeholder="https://acme.com/terms"
							onSaveTitle={(changelogLink2Description) =>
								onSave({
									changelogLink2Description:
										changelogLink2Description.trim() || null,
								})
							}
							onSaveUrl={(raw) =>
								onSave({ changelogLink2Url: normalizeLinkUrl(raw) })
							}
						/>
					</>
				)}
			</SettingsContainer>
		</>
	)
}

// A footer link only renders on the portal when it has both a title and a URL.
function LinkPair({
	label,
	title,
	url,
	placeholder,
	onSaveTitle,
	onSaveUrl,
}: {
	label: string
	title: string
	url: string
	placeholder: string
	onSaveTitle: (value: string) => void
	onSaveUrl: (value: string) => void
}) {
	return (
		<div className="flex flex-col gap-2">
			<div className="text-sm font-medium">{label}</div>
			<div className="flex w-full gap-2">
				<AutosaveInput
					className="w-1/3"
					name={`${label} title`}
					placeholder="Title"
					value={title}
					maxLength={50}
					onSave={onSaveTitle}
				/>
				<AutosaveInput
					className="w-2/3"
					name={`${label} url`}
					placeholder={placeholder}
					value={url}
					maxLength={500}
					validate={isValidLinkUrl}
					onSave={onSaveUrl}
				/>
			</div>
		</div>
	)
}

function AutosaveField({
	label,
	hint,
	placeholder,
	value,
	validate,
	invalidHint,
	onSave,
}: {
	label: string
	hint?: string
	placeholder: string
	value: string
	validate?: (value: string) => boolean
	invalidHint?: string
	onSave: (value: string) => void
}) {
	return (
		<div className="flex flex-col gap-2">
			<div className="flex flex-col gap-0.5">
				<div className="text-sm font-medium">{label}</div>
				{hint && <div className="text-xs text-secondary">{hint}</div>}
			</div>
			<AutosaveInput
				name={label}
				placeholder={placeholder}
				value={value}
				validate={validate}
				invalidHint={invalidHint}
				onSave={onSave}
			/>
		</div>
	)
}

function AutosaveInput({
	name,
	placeholder,
	value,
	maxLength,
	className,
	validate,
	invalidHint,
	onSave,
}: {
	name: string
	placeholder: string
	value: string
	maxLength?: number
	className?: string
	validate?: (value: string) => boolean
	invalidHint?: string
	onSave: (value: string) => void
}) {
	const [draft, setDraft] = useState(value)
	const isInvalid = validate ? !validate(draft) : false

	const flush = useAutosaveText({
		value: draft,
		persistedValue: value,
		onSave,
		enabled: !isInvalid,
	})

	return (
		<div className={cn("flex flex-col gap-1", className)}>
			<Input
				name={name}
				placeholder={placeholder}
				value={draft}
				maxLength={maxLength}
				onChange={(event) => setDraft(event.target.value)}
				onBlur={() => flush()}
				className={cn({ "border-red-500": isInvalid })}
			/>
			{isInvalid && invalidHint && (
				<div className="text-xs text-red-500">{invalidHint}</div>
			)}
		</div>
	)
}
